import { Component, Input, ViewChild, TemplateRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, filter, take } from 'rxjs/operators';
import { MenuPanelItem } from 'app/header-navigation/components/menu-panel/menu-panel.type';
import { BudgetSettingsPageService } from '../budget-settings-page/budget-settings-page.service';
import { BudgetTableService } from '../../services/budget-table.service';
import { BudgetTableContextMenuService } from '../../services/budget-table-context-menu.service';
import { BudgetTableActionType } from '../budget-table/budget-table.types';
import { IconHeaderModalService } from '../../../shared/services/modal-with-icon-header.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { BudgetDataService } from '../../../dashboard/budget-data/budget-data.service';
import { ForecastBudgetService } from '../../services/forecast-budget.service';
import { BudgetService } from '../../../shared/services/backend/budget.service';
import { Budget } from '../../../shared/types/budget.interface';
import { CompanyDataService } from '../../../shared/services/company-data.service';
import { TableColumnId } from '../../../manage-ceg-table/components/manage-ceg-table-container/manage-ceg-table-container.enum';

@Component({
  selector: 'budget-manage-menu',
  templateUrl: './budget-manage-menu.component.html',
  styleUrls: ['./budget-manage-menu.component.scss']
})
export class BudgetManageMenuComponent {
  private destroy$ = new Subject<void>();

  private segmentMenuHandlers = {
    [BudgetTableActionType.SegmentsGroup]: () => this.budgetSettingsPageService.groupSelectedSegments(),
    [BudgetTableActionType.SegmentsUngroup]: () => this.budgetSettingsPageService.ungroupSelectedSegments(),
    [BudgetTableActionType.SegmentsMove]: (group) => this.budgetSettingsPageService.moveSelectedSegments(group),
    [BudgetTableActionType.GroupDuplicate]: () => this.budgetSettingsPageService.duplicateSelectedGroups(),
    [BudgetTableActionType.SegmentDuplicate]: () => this.budgetSettingsPageService.duplicateSelectedSegments(),
    [BudgetTableActionType.SegmentDelete]: () => this.budgetSettingsPageService.removeSelectedSegments(),
  };

  private createBudgetMenuItem: MenuPanelItem = {
    label: 'Create New Budget',
    faIcon: ['fad', 'sack-dollar'],
    action: () => this.budgetSettingsPageService.openBudgetSettingsModal(),
  };

  private customAvailableBudgetCalculation: MenuPanelItem = {
    label: 'Customize Available Budget Calculation',
    faIcon: ['fas', 'function'],
    isDivider: true,
    action: () => this.openCustomAvailableBudgetModal(),
  };

  items: MenuPanelItem[] = [this.createBudgetMenuItem];
  @ViewChild('CABCTemplate') CABCTemplate!: TemplateRef<any>;
  isCegBudget: boolean = false;
  isForecastEnabled: boolean = false;
  budget: Budget | null = null;
  companyId: number;
  nonHighlightedFormulaItem : string[];
  selectionList: { label: string; value: number; selected: boolean; disabled: boolean }[] = [];
  selectedList: number[] = [];
  selectedItems: string[] = [];
  private initialSelectedIds: number[] = [];
  private learnMoreHelpLink: string = 'https://help.planful.com/planful-for-marketing/docs/custom-available-budget';

  customAvailableBudgetCalculationModalData = {
    title: 'Customize Available Budget Calculation',
    content: `<p>Updating the Available Budget calculation will change values across Manage Campaigns, Manage Expense Groups, and their detail view.</p>
<p>You can edit this later if needed, but review all affected areas before proceeding.</p>
<p><a href="${this.learnMoreHelpLink}" target="_blank">Learn More</a></p>`,
    icon: { prefix: 'fad', name: 'function' },
    buttons: [{ text: 'Apply', color: 'primary', disabled: true }]
  };

  @Input() set hasCurrentBudget(value: boolean) {
    this.items = value ? this.generateMenu() : [this.createBudgetMenuItem];
  }

  constructor(
    private readonly budgetSettingsPageService: BudgetSettingsPageService,
    private readonly budgetTableService: BudgetTableService,
    private readonly budgetTableContextMenuService: BudgetTableContextMenuService,
    private modalService: IconHeaderModalService,
    private utilityService: UtilityService,
    private budgetDataService: BudgetDataService,
    private forecastBudgetService: ForecastBudgetService,
    private budgetService: BudgetService,
    private companyDataService: CompanyDataService
  ) {}

  ngOnInit(): void {
    this.budgetDataService.selectedBudget$.subscribe((budget) => {
      this.budget = budget;
      this.isCegBudget = budget?.new_campaigns_programs_structure || false;
      this.items = this.generateMenu();
    });

    this.companyDataService.selectedCompany$
      .pipe(takeUntil(this.destroy$), filter(cmp => cmp != null))
      .subscribe(cmp => this.companyId = cmp.id);

    this.forecastBudgetService.forecastEnabled$.subscribe((forecastEnabled) => {
      this.isForecastEnabled = forecastEnabled;
      this.items = this.generateMenu();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private generateMenu(): MenuPanelItem[] {
    const segmentsMenu = this.budgetTableContextMenuService.generateManageMenu(this.segmentMenuHandlers);

    const menu: MenuPanelItem[] = [
      { label: 'Duplicate Budget', faIcon: ['fad', 'copy'], action: () => this.budgetSettingsPageService.duplicateBudget() },
      { label: 'Edit Budget Details', faIcon: ['fad', 'pen'], isDivider: true, action: () => this.budgetSettingsPageService.editBudget() },
    ];

    menu.push(
      { label: 'Segments:', faIcon: null, isDivider: true, customCssClass: 'group-title' },
      ...segmentsMenu,
      { label: 'Delete Budget', faIcon: ['fad', 'trash-alt'], action: () => this.budgetSettingsPageService.deleteBudget(), isDivider: true, customCssClass: 'item-danger' }
    );

    return menu;
  }

  private updateActualSpendCheckboxState() {
    const forecast = this.selectionList.find(i => i.value === TableColumnId.Forecast);
    const actualSpend = this.selectionList.find(i => i.value === TableColumnId.Actual);
    const committed = this.selectionList.find(i => i.value === TableColumnId.Committed);
    const planned = this.selectionList.find(i => i.value === TableColumnId.Planned);

    let isForecastChecked = this.selectedList.includes(TableColumnId.Forecast);
    let isActualChecked = this.selectedList.includes(TableColumnId.Actual);
    let isCommittedChecked = this.selectedList.includes(TableColumnId.Committed);
    let isPlannedChecked = this.selectedList.includes(TableColumnId.Planned);

    if (this.isForecastEnabled) {

      if (isCommittedChecked || isPlannedChecked) {
        if (actualSpend) {
          actualSpend.selected = true;
          actualSpend.disabled = true;
          if (!isActualChecked){
            this.selectedList.push(TableColumnId.Actual);
            isActualChecked = true;
          }
          if (!this.selectedItems.includes('Actual Spend')) this.selectedItems.splice(1, 0, 'Actual Spend');
        }
        if (forecast) {
          forecast.selected = false;
          forecast.disabled = true;
          if (isForecastChecked) {
            this.selectedList = this.selectedList.filter(id => id !== TableColumnId.Forecast);
            this.selectedItems = this.selectedItems.filter(item => item !== 'Forecast');
          }
          isForecastChecked = false;
        }
        
      } else {
        if (!isForecastChecked && !isActualChecked) return;

        if (actualSpend) {
          actualSpend.disabled = !isForecastChecked;
          if (!isForecastChecked) {
            actualSpend.selected = true;
            if (!isActualChecked) this.selectedList.push(TableColumnId.Actual);
            if (!this.selectedItems.includes('Actual Spend')) this.selectedItems.splice(1, 0, 'Actual Spend');
          }
        }
        if (forecast) forecast.disabled = !isActualChecked;
      }
      
      if (isActualChecked && isPlannedChecked && !isCommittedChecked && !isForecastChecked) {
        setTimeout(() => {
          committed.selected = true;
          committed.disabled = true;
          if (!this.selectedList.includes(TableColumnId.Committed)) {
            this.selectedList.push(TableColumnId.Committed);
          }
          if (!this.selectedItems.includes('Remaining Committed Budget')) {
            this.selectedItems.push('Remaining Committed Budget');
          }
        });
      }

      committed.disabled = isPlannedChecked ? isCommittedChecked : false;

    } else {
      if (planned) {

        if(isPlannedChecked) {
          setTimeout(() => {
            committed.selected = true;
            committed.disabled = true;
            if (!this.selectedList.includes(TableColumnId.Committed)) {
              this.selectedList.push(TableColumnId.Committed);
            }
            if (!this.selectedItems.includes('Remaining Committed Budget')) {
              this.selectedItems.push('Remaining Committed Budget');
            }
          });
        } else {
          committed.disabled = false;
        }
      }
    }

    this.sortSelectedItems();
  }

  private checkIfSelectionChanged() {
    let currentSelected = this.selectionList.filter(i => i.selected).map(i => Number(i.value));
    currentSelected = [1, ...currentSelected.filter(id => id !== 1)];
    if (!this.isForecastEnabled) currentSelected = [1, 3, ...currentSelected.filter(id => id !== 3)];

    const initSet = new Set(this.initialSelectedIds);
    const currSet = new Set(currentSelected);
    const changed = initSet.size !== currSet.size || [...initSet].some(id => !currSet.has(id));

    this.customAvailableBudgetCalculationModalData.buttons[0].disabled = !changed;
  }

  private updateCABStateAndCheckChanges() {
    this.updateActualSpendCheckboxState();
    this.checkIfSelectionChanged();
  }

  openCustomAvailableBudgetModal() {
    if (!this.companyId || !this.budget?.id) return;

    this.budgetService.getBudgetsCABFormula(this.companyId, this.budget.id).subscribe((response) => {
      const selectedFieldIds = response.map(item => item.manage_page_fields);
      const selectedFieldNames = response.map(item => item.name);
      this.initialSelectedIds = response.map(item => item.manage_page_fields);

      this.selectedItems = [];
      if (selectedFieldNames.includes('Total Budget')) this.selectedItems.push('Total Budget');
      if (selectedFieldNames.includes('Actual Spend')) this.selectedItems.push('Actual Spend');
      this.selectedItems.push(...selectedFieldNames.filter(n => n !== 'Total Budget' && n !== 'Actual Spend'));

      const defaultItems = this.isForecastEnabled
      ? [
          { label: 'Forecast', value: TableColumnId.Forecast },
          { label: 'Remaining Committed Budget', value: TableColumnId.Committed },
          { label: 'Actual Spend', value: TableColumnId.Actual },
          { label: 'Remaining Planned Budget', value: TableColumnId.Planned }
        ]
      : [
          { label: 'Remaining Committed Budget', value: TableColumnId.Committed },
          { label: 'Remaining Planned Budget', value: TableColumnId.Planned }
        ];

        if(!this.isForecastEnabled){
          this.nonHighlightedFormulaItem = ['Total Budget', 'Actual Spend', 'Available Budget'];
        }
        else{
          this.nonHighlightedFormulaItem = ['Total Budget', 'Available Budget'];
        }

      this.selectionList = defaultItems.map(item => ({
        ...item,
        selected: selectedFieldIds.includes(item.value),
        disabled: false
      }));

      this.selectedList = this.selectionList.filter(i => i.selected).map(i => i.value);
      this.updateCABStateAndCheckChanges();

      this.modalService.openInitialUpdatePopup(
        this.customAvailableBudgetCalculationModalData,
        this.CABCTemplate,
        false,
        'custom-calculation',
        false,
        false,
        () => {},
        '630px'
      ).pipe(take(1)).subscribe((result) => {
        if (result ) {
          let selectedIds: number[] = [TableColumnId.Budget];
          if (this.isForecastEnabled) {
            selectedIds = [
              TableColumnId.Budget,
              ...this.selectedList.filter(id => id !== TableColumnId.Budget)
            ];
          } else {
            selectedIds = [
              TableColumnId.Budget,
              TableColumnId.Actual,
              ...this.selectedList.filter(id => id !== TableColumnId.Budget && id !== TableColumnId.Actual)
            ];
          }

          const isCreate = response.some(item => item.budget === null);

          const request$ = isCreate
            ? this.budgetService.addBudgetsCABFormula(this.budget.id, this.companyId, selectedIds)
            : this.budgetService.updateBudgetsCABFormula(this.budget.id, this.companyId, selectedIds);

          request$.subscribe(() => {
            this.utilityService.showToast({ Title: '', Message: 'Changes have been saved', Type: 'success' });
          });
        }
      });
    });
  }

  private sortSelectedItems(): void {
    const labelOrder = [
      'Total Budget',
      'Forecast',
      'Actual Spend',
      'Remaining Committed Budget',
      'Remaining Planned Budget'
    ];
  
    const labelToValueMap = new Map(this.selectionList.map(i => [i.label, i.value]));
  
    this.selectedItems.sort((a, b) => labelOrder.indexOf(a) - labelOrder.indexOf(b));
    this.selectedList.sort();
  }

  onCheckboxChange(event: boolean, item: any) {
    const label = item.label;
    const value = item.value;
    item.selected = event;
  
    if (event) {
      if (!this.selectedList.includes(value)) {
        this.selectedList.push(value);
      }
      if (!this.selectedItems.includes(label)) {
        this.selectedItems.push(label);
      }
    } else {
      this.selectedList = this.selectedList.filter(v => v !== value);
      this.selectedItems = this.selectedItems.filter(l => l !== label);
    }
  
    this.updateCABStateAndCheckChanges();
  }  
}